import { useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import { TermPart } from "@models/ISchema";
import { IParams } from "@models/IParams";
import { TermPartsByCategory } from "../ISections";
import { getTermParts } from "../graphql/termParts.query";

export interface IUseGetTermPartsByCategoryProps {
  linkId?: string;
  onMount: (termPartsByCategory: TermPartsByCategory[]) => void;
}

export const useGetTermPartsByCategory = ({
  onMount,
  linkId,
}: IUseGetTermPartsByCategoryProps): [TermPartsByCategory[], () => void] => {
  const { scenario }: IParams = useParams();
  const client = useApolloClient();
  const [termPartsByCategory, setTermPartsByCategory] = useState<TermPartsByCategory[]>(null);

  const variables = {
    scenarioId: scenario,
    filter: linkId && {
      fields: {
        weeksByLinkId: {
          eq: linkId,
        },
      },
    },
  };

  const getTermPartsByCategoryInfo = async () => {
    try {
      const { data, errors } = await client.query({
        query: getTermParts,
        variables,
        fetchPolicy: "no-cache",
      });

      // if errors exists, console.error them and return right away
      if (errors) {
        console.error("[GetTermPartsByCategory error]", errors);
        return null;
      }

      // define two objects:
      //    - tpByCategories: array of objects where each key is a `categoryId`
      //          and the value is an array of the termParts of that category.
      //    - categoriesById: object where each key is a `categoryId` and each
      //          value is the category itself.
      const { tpByCategories, categoriesById } = (data?.data?.termParts?.items ?? []).reduce(
        (
          acc: {
            tpByCategories: Record<string, TermPart[]>;
            categoriesById: Record<string, TermPart["category"]>;
          },
          tp: TermPart,
        ) => {
          acc.categoriesById[tp.category.id] = tp.category;
          if (!(tp.category.id in acc.tpByCategories)) acc.tpByCategories[tp.category.id] = [];
          acc.tpByCategories[tp.category.id].push(tp);
          return acc;
        },
        { tpByCategories: {}, categoriesById: {} },
      );

      // define and set the termPartsByCategory
      const newTermPartsByCategory: TermPartsByCategory[] = Object.entries(
        tpByCategories as Record<string, TermPart[]>,
      ).map(([categoryId, termParts]) => ({
        category: categoriesById[categoryId],
        termParts,
      }));
      setTermPartsByCategory(newTermPartsByCategory);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (termPartsByCategory === null || termPartsByCategory.length > 0) {
      onMount(termPartsByCategory);
    }
  }, []);

  return [termPartsByCategory, getTermPartsByCategoryInfo];
};
